/* VUE
-------------------------------------------------------------- */
import Vue from 'vue';

/* VUEX
-------------------------------------------------------------- */
import Vuex from 'vuex';

Vue.use(Vuex);

/* VUE-AXIOS
-------------------------------------------------------------- */
import axios from 'axios';
import VueAxios from 'vue-axios';
import router from '@/router';
import store from '@/store';
import { redirectToKeycloak, getStoredToken, clearTokens, logoutFromKeycloak } from '@/services/keycloak';

Vue.use(Vuex, VueAxios, axios);

let pendingUserRequest: Promise<void> | null = null;

/**
 * Fetch logged in user details and store it.
 * Requires a valid access token in localStorage (set after Keycloak callback).
 *
 * @return     {Promise}  { Promise of getting user details }
 */
export const fetchLoggedInUser = (context: any) => {
  const keycloakEnabled = process.env.VUE_APP_KEYCLOAK_ENABLED === 'true';

  if (keycloakEnabled && !getStoredToken()) {
    return Promise.reject();
  }

  // Share a single in-flight request, so parallel route navigations (guard + callback)
  // don't each hit /user.info and produce duplicate failures on the backend.
  if (pendingUserRequest) {
    return pendingUserRequest;
  }

  pendingUserRequest = new Promise<void>((resolve, reject) => {
    axios
      .get(
        '/-/trade/api/user.info',
      ).then(
      (response) => {
        store.commit('SET_IS_AUTHENTICATED', true);
        context.commit('SET_USER', response.data);
        resolve(undefined);
      },
      (error) => {
        // The backend answered that this token doesn't belong to a usable user
        // (401 = rejected, 404 = no user info). Retrying with the same token can never
        // succeed, so drop it; otherwise every navigation would call /user.info again.
        const status = error?.response?.status;
        if (keycloakEnabled && (status === 401 || status === 404)) {
          clearTokens();
        }
        reject();
      },
    );
  }).finally(() => {
    pendingUserRequest = null;
  });

  return pendingUserRequest;
};

/**
 * Check user existence and Keycloak migration status by email.
 * Only used when Keycloak is enabled.
 *
 * Resolves with the backend status: 'redirect_keycloak' | 'needs_migration'.
 *
 * @return     {Promise}  { Promise resolving with the AuthCheckStatus value }
 */
export const checkUser = (context: any, email: string) => {
  return new Promise<string>((resolve, reject) => {
    axios
      .post(
        '/-/trade/api/auth.check',
        { email },
      ).then(
      (response) => {
        resolve(response.data.status);
      },
      (error) => {
        reject(error);
      },
    );
  });
};

/**
 * Log in / migrate user:
 * - if Keycloak is enabled: migrates the user to Keycloak (using the provided password),
 *   then redirects to the Keycloak login page
 * - if Keycloak is disabled: authenticates via email/password and starts a basic web auth session
 *
 * @return     {Promise}  { Promise of the login/migration process }
 */
export const authorizeUser = (context: any, userDetails: any) => {
  const keycloakEnabled = process.env.VUE_APP_KEYCLOAK_ENABLED === 'true';

  return new Promise<any>((resolve, reject) => {
    const payload: any = { email: userDetails.email, password: userDetails.password };

    axios
      .post(
        '/-/trade/api/auth.login',
        payload,
      ).then(
      (response) => {
        if (keycloakEnabled) {
          // let Keycloak silently reuse an existing SSO session if there is one,
          // so the user isn't forced to re-enter their credentials every time
          redirectToKeycloak(userDetails.email);
          resolve(undefined);
        } else {
          axios.get('/-/trade/api/user.info').then(
            (userResponse) => {
              store.commit('SET_IS_AUTHENTICATED', true);
              context.commit('SET_USER', userResponse.data);
              resolve(undefined);
            },
            () => {
              store.commit('SET_IS_AUTHENTICATED', true);
              context.commit('SET_USER', response.data);
              resolve(undefined);
            },
          );
        }
      },
      (error) => {
        // tslint:disable-next-line:no-console
        console.error(error);
        reject(error);
      },
    );
  });
};

/**
 * Trigger the Keycloak "forgot password" migration for a not-yet-migrated user.
 * Migrates the user into Keycloak without a password, so Keycloak emails the
 * verified address on file a link to set their password (UPDATE_PASSWORD +
 * CONFIGURE_TOTP). Only used when Keycloak is enabled.
 *
 * @return     {Promise}  { Promise of the forgot password migration request }
 */
export const forgotPasswordUser = (context: any, email: string) => {
  return new Promise<void>((resolve, reject) => {
    axios
      .post(
        '/-/trade/api/auth.password.forgot',
        { email },
      ).then(
      () => {
        resolve(undefined);
      },
      (error) => {
        reject(error);
      },
    );
  });
};

/**
 * Logout user — clear tokens and redirect to login.
 *
 * When Keycloak is enabled, local auth state is intentionally NOT cleared
 * upfront. It is only cleared in the logout callback route, which Keycloak
 * only redirects to after the user explicitly confirms the logout. This
 * prevents the WS Portal session from being invalidated if the user cancels
 * the logout on Keycloak's confirmation screen.
 */
export const logoutUser = () => {
  const keycloakEnabled = process.env.VUE_APP_KEYCLOAK_ENABLED === 'true';
  if (keycloakEnabled) {
    const logoutCallbackUrl = `${window.location.origin}${router.resolve({ name: 'auth.logoutCallback' }).href}`;
    logoutFromKeycloak(logoutCallbackUrl);
    return;
  }

  store.commit('SET_IS_AUTHENTICATED', false);
  const loginUrl = `${window.location.origin}${router.resolve({ name: 'auth.login' }).href}`;
  axios.post('/-/trade/api/auth.logout').finally(() => {
    window.location.href = loginUrl;
  });
};
